import type React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@lib/utils';
import { POLL_COPY } from './pollCopy';
import type { PollFooterRight } from './pollDisplay';
import type { PollTime } from './pollFormat';

export interface PollFooterProps {
  time: PollTime;
  right: PollFooterRight;
  /** Opens "Who answered" (B6); the line is its button only when the viewer may see names. */
  onSeeWho?: () => void;
}

const rightText = (right: PollFooterRight): string => {
  switch (right.kind) {
    case 'vote-to-see':
      return POLL_COPY.voteToSeeResults;
    case 'no-answers-yet':
      return POLL_COPY.noAnswersYet;
    case 'no-one-answered':
      return POLL_COPY.noOneAnswered;
    case 'held':
      return POLL_COPY.countsOnceYoureIn;
    case 'none':
      return '';
    case 'answered':
      return right.seeWho
        ? POLL_COPY.peopleAnsweredSeeWho(right.count)
        : POLL_COPY.peopleAnswered(right.count);
  }
};

const rightClass = (right: PollFooterRight): string => {
  if (right.kind === 'answered') {
    return right.seeWho ? 'font-medium text-mb-teal-ink' : 'font-normal text-mb-dark-gray';
  }
  if (right.kind === 'held') return 'font-medium text-mb-dark-gray';
  return 'font-normal text-mb-grey-300';
};

/**
 * The card's footer (polls-B1 "Footer"): the time on the left with its clock (hidden beside "No end
 * time"), the answer state on the right. The time is never announced as it ticks (TECH-197 § Edge
 * cases "Accessibility"); `PollCardBody` announces the end once.
 *
 * "{n} people answered · See who" is one button, only with `can_see_voters` (TECH-197 § Voting):
 * 44px tall around its 16px line, the extra drawn into the gap around the footer (negative margin),
 * so the card keeps its spacing.
 */
export const PollFooter: React.FC<PollFooterProps> = ({ time, right, onSeeWho }) => (
  <div className="flex items-center justify-between gap-3 text-xs leading-4">
    <span
      data-testid="poll-footer-time"
      className={cn(
        'flex min-w-0 items-center gap-1.5',
        time.showClock ? 'font-medium text-mb-dark-gray' : 'font-normal text-mb-grey-300',
      )}
    >
      {time.showClock && (
        <Clock aria-hidden="true" className="size-3.5 shrink-0 text-mb-dark-gray" strokeWidth={2} />
      )}
      <span>{time.text}</span>
    </span>
    {right.kind === 'answered' && right.seeWho && onSeeWho ? (
      <button
        type="button"
        data-testid="poll-footer-right"
        className={cn(
          '-my-3.5 inline-flex min-h-11 shrink-0 items-center rounded-sm text-right focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink',
          rightClass(right),
        )}
        onClick={event => {
          // The card's own click would open the Post sheet.
          event.stopPropagation();
          onSeeWho();
        }}
      >
        {rightText(right)}
      </button>
    ) : right.kind === 'none' ? null : (
      <span
        data-testid="poll-footer-right"
        className={cn('shrink-0 text-right', rightClass(right))}
      >
        {rightText(right)}
      </span>
    )}
  </div>
);
